import { Avatar, Box, Button, CircularProgress, Divider, ListItemIcon, Menu, MenuItem, Toolbar } from "@mui/material";
import MuiAppBar from "@mui/material/AppBar";
import CssBaseline from "@mui/material/CssBaseline";
import { styled } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Logout from "@mui/icons-material/Logout";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { LanguagesDropdown } from "components/LanguagesDropdown";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { LocalStorageGlobalKeys } from "constants/constants";
import { logout } from "api/queries/loginAPI";
import { removeUser, resetLocalStorage } from "utilities/storageUtilities";
import ProfileModal from "components/Layout/AppDrawer/components/AppToolbar/components/ProfileModal/ProfileModal.view";
import { paperProps, useStyles } from "./ApiAdminLayout.styles";
import { IApiAdminLayoutProps } from "./ApiAdminLayoutInterfaces";

const AppBar = styled(MuiAppBar)(({ theme }) => ({
    backgroundColor: "rgba(255, 255, 255, 0.94)",
    backdropFilter: "blur(10px)",
    color: theme.palette.menu.icons.main,
    borderBottom: `1px solid ${theme.palette.border.select}`,
}));

const Main = styled("main")({
    flexGrow: 1,
    minWidth: 0,
});

/**
 * Chrome for the API Administrator portal (OS-2557).
 *
 * Deliberately not the CBO Layout/AppToolbar: this user has exactly one page, so there is no
 * Locations dropdown, no Settings, no Help, no Aspen/Birch switcher and no Madera-BO return —
 * every one of which would navigate somewhere the role cannot reach. Only Profile, Logout and
 * the language switcher remain, plus ProfileModal, which is REST-only and needs no Hasura grants.
 */
const ApiAdminLayout = ({ children }: IApiAdminLayoutProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { currentUser, isLoadingCurrentUser, refreshCurrentUser } = useContext(CorporateContext);
    const [isOpenProfileModal, setOpenProfileModal] = useState(false);
    const [anchorElForUserMenu, setAnchorElForUserMenu] = useState<null | HTMLElement>(null);
    const isOpenUserMenu = Boolean(anchorElForUserMenu);

    const handleUserMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        refreshCurrentUser();
        setAnchorElForUserMenu(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setAnchorElForUserMenu(null);
    };

    const handleClickProfile = () => {
        setOpenProfileModal(true);
        handleUserMenuClose();
    };

    const handleLogout = () => {
        removeUser();
        logout();
        handleUserMenuClose();
        resetLocalStorage([LocalStorageGlobalKeys.LANGUAGES, LocalStorageGlobalKeys.I18_LANGUAGE_SELECTION]);
        window.location.reload();
    };

    return (
        <Box className={classes.mainBox}>
            <CssBaseline />
            <AppBar position="fixed" elevation={0}>
                <Toolbar>
                    <div className={classes.appBar}>
                        <div className={classes.appBarLeftContent}>
                            <img src="/images/maderaLogoTrimmed.png" alt="Madera" className={classes.brandLogo} />
                            <Divider orientation="vertical" flexItem className={classes.divider} />
                            <span className={classes.portalName}>{t("api_administration.portal_name")}</span>
                        </div>
                        <div className={classes.appBarRightContent}>
                            <LanguagesDropdown className={classes.appBarLanguageDropdown} />
                            <Divider orientation="vertical" flexItem className={classes.divider} />
                            <Button
                                id="basic-button"
                                aria-controls={isOpenUserMenu ? "basic-menu" : undefined}
                                aria-haspopup="true"
                                aria-expanded={isOpenUserMenu ? "true" : undefined}
                                onClick={handleUserMenuClick}
                                className={classes.menuButton}
                                disableRipple
                            >
                                {isLoadingCurrentUser && <CircularProgress />}
                                {/*
                                 * The avatar and caret render regardless of whether the profile
                                 * resolved: this menu is the only route to Logout, so a failed or
                                 * empty profile read must not leave an unlabelled, undiscoverable
                                 * control. The name is omitted rather than printed as "undefined
                                 * undefined" when the fields are absent.
                                 */}
                                {!isLoadingCurrentUser && (
                                    <>
                                        <Avatar className={classes.avatar} src={currentUser?.media?.media_url} />
                                        {[currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(" ")}
                                        <ExpandMoreIcon
                                            className={
                                                isOpenUserMenu ? classes.expandMoreIconRotated : classes.expandMoreIcon
                                            }
                                        />
                                    </>
                                )}
                            </Button>
                            <Menu
                                anchorEl={anchorElForUserMenu}
                                id="basic-menu"
                                open={isOpenUserMenu}
                                onClose={handleUserMenuClose}
                                onClick={handleUserMenuClose}
                                PaperProps={paperProps}
                                transformOrigin={{ horizontal: "right", vertical: "top" }}
                                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                            >
                                <MenuItem onClick={handleClickProfile}>
                                    <ListItemIcon>
                                        <Avatar className={classes.avatar} src={currentUser?.media?.media_url} />
                                    </ListItemIcon>
                                    {t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile")}
                                </MenuItem>
                                <Divider />
                                <MenuItem onClick={handleLogout}>
                                    <ListItemIcon>
                                        <Logout fontSize="small" />
                                    </ListItemIcon>
                                    {t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.logout")}
                                </MenuItem>
                            </Menu>
                        </div>
                    </div>
                    {isOpenProfileModal && (
                        <ProfileModal
                            open={isOpenProfileModal}
                            user={currentUser}
                            onClose={() => setOpenProfileModal(false)}
                        />
                    )}
                </Toolbar>
            </AppBar>
            <Main>{children}</Main>
        </Box>
    );
};

export default ApiAdminLayout;
